import * as React from 'react'
import { isNull } from 'lodash'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import AppBadge from './app-badge'

type RiskBadgeProps = {
  count: number,
  showCount?: boolean,
  mid?: number,
  high?: number
}

function RiskBadge({ count, showCount = false, mid = 1, high = 2 }: RiskBadgeProps) {
  const riskTooltip = 'Transaction risk based on your analytic weighting'

  // Every path below must return. Previously only `undefined` was handled, so
  // any value that failed all three comparisons — NaN from a non-numeric
  // risk_score, or simply a negative one — fell off the end of the function and
  // crashed React with "Nothing was returned from render", taking the whole
  // drawer down with it.
  if (count === undefined) {
    return <React.Fragment></React.Fragment>
  }

  // null has always meant "no flags yet", i.e. low risk.
  const score = isNull(count) ? 0 : Number(count)

  if (isNaN(score)) {
    return <React.Fragment></React.Fragment>
  }

  if (score >= high) {
    return (
      <OverlayTrigger
        placement="top"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip>{riskTooltip}</Tooltip>}
      >
        <AppBadge
          pill
          variant="danger"
          className="text-center text-danger high-risk fw-normal"
          style={{ cursor: 'help' }}
          data-testid={'risk-badge'}
        >
          {showCount ? count : 'High Risk'}
        </AppBadge>
      </OverlayTrigger>
    )
  }

  if (score >= mid) {
    return (
      <OverlayTrigger
        placement="top"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip>{riskTooltip}</Tooltip>}
      >
        <AppBadge
          pill
          variant="warning"
          className="text-center fw-normal"
          style={{ cursor: 'help' }}
          data-testid={'risk-badge'}
        >
          {showCount ? count : 'Med Risk'}
        </AppBadge>
      </OverlayTrigger>
    )
  }

  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 250, hide: 400 }}
      overlay={<Tooltip>{riskTooltip}</Tooltip>}
    >
      <AppBadge
        pill
        variant="success"
        className="text-center text-success fw-normal"
        style={{ cursor: 'help' }}
        data-testid={'risk-badge'}
      >
        {showCount ? count : 'Low Risk'}
      </AppBadge>
    </OverlayTrigger>
  )
}

export default RiskBadge
